import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import scrollRestoration from '../utils/scroll-restoration';

/**
 * Custom hook that handles scroll position saving and restoration
 * @param {Object} params - Configuration object
 * @param {string} params.mediaType - Type of media ('movies' or 'tv')
 * @param {string} params.searchQuery - Current search query
 * @param {Object} params.filters - Current filters
 * @param {boolean} params.isLoading - Whether data is loading
 * @param {number} params.itemsLength - Number of items loaded
 * @returns {string} locationKey - Unique key for this view
 */
export function useScrollRestoration({ mediaType, searchQuery, filters, isLoading, itemsLength }) {
  const location = useLocation();
  const hasRestoredScroll = useRef(false);
  const prevFiltersRef = useRef(filters);
  const prevSearchRef = useRef(searchQuery);
  const prevPathnameRef = useRef(location.pathname);
  const isInitialMount = useRef(true);
  const viewportWidthRef = useRef(window.innerWidth);
  
  // Create unique location key
  const locationKey = `${mediaType}-${location.pathname}-${searchQuery || 'browse'}-${JSON.stringify(filters)}`;
  
  // Create a key that doesn't include filters (for detecting same-page filter changes)
  const pageKey = `${mediaType}-${location.pathname}-${searchQuery || 'browse'}`;
  const prevPageKeyRef = useRef(pageKey);

  // Handle viewport changes - invalidate scroll restoration if viewport changes significantly
  useEffect(() => {
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const prevWidth = viewportWidthRef.current;
      
      // Define breakpoints that match the CSS media queries
      const getBreakpoint = (width) => {
        if (width < 640) return 'mobile';
        if (width < 768) return 'sm-tablet';
        if (width < 1024) return 'tablet';
        if (width < 1280) return 'desktop';
        return 'large';
      };
      
      const prevBreakpoint = getBreakpoint(prevWidth);
      const currentBreakpoint = getBreakpoint(currentWidth);
      
      // If breakpoint changed, invalidate scroll restoration
      if (prevBreakpoint !== currentBreakpoint) {
        hasRestoredScroll.current = false;
        // Clear saved scroll position as layout has changed
        scrollRestoration.clearScrollPosition(locationKey);
      }
      
      viewportWidthRef.current = currentWidth;
    };

    const handleOrientationChange = () => {
      // Give the browser a moment to reflow after orientation change
      setTimeout(() => {
        hasRestoredScroll.current = false;
        scrollRestoration.clearScrollPosition(locationKey);
        viewportWidthRef.current = window.innerWidth;
      }, 100);
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleOrientationChange);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleOrientationChange);
    };
  }, [locationKey]);

  useEffect(() => {
    let isActive = true;
    
    const handleScroll = () => {
      if (!isActive) return;
      scrollRestoration.saveScrollPosition(locationKey, window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      isActive = false;
      window.removeEventListener('scroll', handleScroll);
    };
  }, [locationKey]);

  // Track page key and reset restoration flag when filters/search/pathname change
  useEffect(() => {
    const filtersChanged = JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);
    const searchChanged = prevSearchRef.current !== searchQuery;
    const pathnameChanged = prevPathnameRef.current !== location.pathname;
    const pageKeyChanged = prevPageKeyRef.current !== pageKey;
    
    if (filtersChanged || searchChanged || pathnameChanged) {
      // Mark that initial mount is complete after first change
      if (isInitialMount.current) {
        isInitialMount.current = false;
      }
      
      // Only reset restoration flag if it's a new page (not just filter change)
      if (pageKeyChanged) {
        hasRestoredScroll.current = false;
      } else if (filtersChanged) {
        // Filter change on same page - preserve scroll, but reset flag to allow restoration check
        hasRestoredScroll.current = false;
      }
      
      prevFiltersRef.current = filters;
      prevSearchRef.current = searchQuery;
      prevPathnameRef.current = location.pathname;
      prevPageKeyRef.current = pageKey;
    }
  }, [searchQuery, filters, location.pathname, pageKey]);

  // Restore scroll position when navigating back from detail page
  useEffect(() => {
    let timeoutId = null;
    let isCleanedUp = false;
    
    if (!isLoading && itemsLength > 0 && !hasRestoredScroll.current) {
      const lastListView = scrollRestoration.getLastListView();
      
      if (lastListView === locationKey) {
        // For tablets, ensure content is fully rendered before restoration
        const isTablet = window.innerWidth >= 640 && window.innerWidth < 1024;
        
        if (isTablet) {
          // Wait for content to be ready on tablets
          let attempts = 0;
          const maxAttempts = 40; // Max 2 seconds (40 * 50ms)
          
          const attemptRestore = () => {
            if (isCleanedUp) return; // Stop if component unmounted
            
            attempts++;
            
            // Check if scroll height is sufficient (content is rendered)
            const hasContent = document.body.scrollHeight > window.innerHeight;
            const savedPosition = scrollRestoration.getScrollPosition(locationKey);
            
            // Also check if we have enough content for the saved position
            const hasEnoughContent = savedPosition ? document.body.scrollHeight > savedPosition : hasContent;
            
            if (hasEnoughContent || attempts >= maxAttempts) {
              // Content is ready or we've waited long enough, proceed with restoration
              const restored = scrollRestoration.restoreScrollPosition(locationKey, () => {
                scrollRestoration.clearNavigationState();
              });
              
              // Only mark as restored if it actually worked
              if (restored) {
                hasRestoredScroll.current = true;
              }
            } else {
              // Content not fully rendered yet, try again
              timeoutId = setTimeout(attemptRestore, 50);
            }
          };
          
          // Start restoration attempt after a small delay
          timeoutId = setTimeout(attemptRestore, 150);
        } else {
          // Mobile and desktop can restore with minimal delay
          requestAnimationFrame(() => {
            if (isCleanedUp) return; // Stop if component unmounted
            
            const restored = scrollRestoration.restoreScrollPosition(locationKey, () => {
              scrollRestoration.clearNavigationState();
            });
            
            // Only mark as restored if it actually worked
            if (restored) {
              hasRestoredScroll.current = true;
            }
          });
        }
      } else {
        // Check if only filters changed (same page, same search)
        // Use the current ref values which are updated in the tracking effect above
        const isFilterChangeOnly = prevPageKeyRef.current === pageKey && !isInitialMount.current;
        
        if (isFilterChangeOnly) {
          // Preserve scroll position when only filters change
          // Don't scroll to top - let the user stay where they are
          hasRestoredScroll.current = true;
        } else {
          // New view (different pathname or search) or initial mount, scroll to top
          window.scrollTo(0, 0);
          hasRestoredScroll.current = true;
        }
      }
    }
    
    // Cleanup function to prevent memory leaks
    return () => {
      isCleanedUp = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isLoading, itemsLength, locationKey, pageKey]);

  return locationKey;
}
